import React, { useRef, useEffect, useState } from 'react'
import FallbackLogo from '../FallbackLogo'

export default function VideoSlide({
  src,
  isActive,
  onEnded,
  onError,
  loop,
  preload = 'metadata',
  poster,
}) {
  const videoRef = useRef(null)
  const onErrorRef = useRef(onError)
  const hasLoadedFrameRef = useRef(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // Reset ready state whenever the source changes
  useEffect(() => {
    hasLoadedFrameRef.current = false
    setIsReady(false)
    if (videoRef.current) {
      videoRef.current.muted = false
    }
  }, [src])

  useEffect(() => {
    const video = videoRef.current
    if (!video || preload !== 'auto') return

    if (video.readyState < 2) {
      video.load()
    }
  }, [preload, src])

  // Start playback as soon as the slide is active so the browser can buffer early.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (!isActive) {
      video.pause()
      return
    }

    if (video.readyState < 2) {
      video.load()
    }

    if (!isReady && video.readyState >= 2) {
      setIsReady(true)
    }

    const playPromise = video.play()
    if (!playPromise?.catch) return

    playPromise.catch(err => {
      if (err?.name === 'AbortError') return

      if (err?.name === 'NotAllowedError') {
        video.muted = true
        const mutedPlayPromise = video.play()

        if (!mutedPlayPromise?.catch) return

        mutedPlayPromise.catch(retryErr => {
          if (retryErr?.name === 'AbortError') return

          console.error('Error playing video:', retryErr)
          onErrorRef.current?.()
        })
        return
      }

      console.error('Error playing video:', err)
      onErrorRef.current?.()
    })
  }, [isActive, isReady, src])

  // Cleanup on unmount
  useEffect(() => {
    const video = videoRef.current

    return () => {
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [])

  const handleVideoReady = () => {
    hasLoadedFrameRef.current = true
    setIsReady(true)
  }

  const handleVideoWaiting = () => {
    if (!hasLoadedFrameRef.current) {
      setIsReady(false)
    }
  }

  return (
    <div
      className={`media-slide video-slide ${isActive ? 'active' : ''}`}
      data-testid="video-slide">
      {/* Show company logo until the first video frame is available */}
      {isActive && !isReady && <FallbackLogo />}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        preload={preload}
        autoPlay={isActive}
        loop={loop}
        onEnded={onEnded}
        onError={() => onErrorRef.current?.()}
        onLoadedData={handleVideoReady}
        onCanPlay={handleVideoReady}
        onWaiting={handleVideoWaiting}
        onPlaying={handleVideoReady}
        playsInline
        style={{
          width: '100%',
          height: '100vh',
          objectFit: 'cover',
          display: 'block',
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }}
      />
    </div>
  )
}
